import { ModalService, SimpleMessageService, Tenant, WebAppCommonModule } from '@allbesmart/web-app-common';
import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { first, Subscription } from 'rxjs';
import { ResetRanConfigurationWarningModalComponent } from "../../components/reset-ran-configuration-warning-modal/reset-ran-configuration-warning-modal.component";
import { RanConfigurationService } from "../../services/ran-configuration.service";
import { TenantManagementService } from "../../services/tenant-management.service";
import { RanConfigurationDto } from "../../models/ran-configuration/ran-configuration-dto";
import { AvailableRoutes } from "../../enums/available-routes";
import { UserRole, WebAppKeycloakAuthModule } from '@allbesmart/web-app-keycloak-auth';
import { RanConfigurationFormComponent } from '../../components/ran-configuration-form/ran-configuration-form.component';

@Component({
    selector: 'oaibox-gnb-custom-configuration-page',
    templateUrl: './gnb-custom-configuration-page.component.html',
    styleUrls: ['./gnb-custom-configuration-page.component.css'],
    imports: [RanConfigurationFormComponent, WebAppCommonModule, WebAppKeycloakAuthModule]
})
export class GnbCustomConfigurationPageComponent implements OnDestroy {

  protected readonly UserRole = UserRole;

  public tenant?: Tenant;
  private tenantSubscription?: Subscription;

  public customRanConfiguration?: RanConfigurationDto;
  private modalClosedSubscription?: Subscription;
  private fetchCustomRanConfigurationSubscription?: Subscription;
  private createUpdateConfigurationSubscription?: Subscription;

  protected requestOnGoing: boolean = false;

  constructor(
    tenantManagementService: TenantManagementService,
    activatedRoute: ActivatedRoute,
    private router: Router,
    private modalService: ModalService,
    private simpleMessageService: SimpleMessageService,
    private ranConfigurationService: RanConfigurationService,
  ) {
    const tenantId = activatedRoute.snapshot.paramMap.get('tenantId');
    if (tenantId) this.tenantSubscription = tenantManagementService.fetchTenant(tenantId).subscribe(tenant => {
      this.tenant = tenant;
      this.refreshCustomConfiguration();
    });
  }

  protected updateConfiguration(ranConfigurationDto: RanConfigurationDto) {
    this.customRanConfiguration = ranConfigurationDto;
  }

  private refreshCustomConfiguration(): void {
    if (this.tenant) {
      this.fetchCustomRanConfigurationSubscription?.unsubscribe();
      this.fetchCustomRanConfigurationSubscription = this.ranConfigurationService.fetchCustomRanConfiguration(this.tenant.id)
        .subscribe(customConfiguration => this.customRanConfiguration = customConfiguration);
    }
  }

  protected save(): void {
    if (this.tenant && this.customRanConfiguration) {
      this.requestOnGoing = true;
      this.createUpdateConfigurationSubscription?.unsubscribe();
      this.createUpdateConfigurationSubscription = this.ranConfigurationService
        .createOrUpdateRanCustomConfiguration(this.customRanConfiguration, this.tenant.id)
        .subscribe({
          next: () => {
            this.requestOnGoing = false;
            this.simpleMessageService.showSimpleMessage({
              message: 'Configuration saved successfully!', icon: 'fa-solid fa-check-double', color: 'success',
            });
          },
          error: () => {
            this.requestOnGoing = false;
            this.simpleMessageService.showSimpleMessage({
              message: 'Configuration not saved!', icon: 'fa-solid fa-triangle-exclamation', color: 'danger',
            });
          },
        });
    }
  }

  protected goBack(): void {
    this.router.navigate([AvailableRoutes.ADMIN, AvailableRoutes.DEPLOYMENTS]);
  }

  protected resetConfiguration(): void {
    this.modalService.pushModal({
      component: ResetRanConfigurationWarningModalComponent,
      inputs: new Map<string, any>([['tenant', this.tenant]])
    });
    this.modalClosedSubscription?.unsubscribe();
    this.modalClosedSubscription = this.modalService.modalClose$().pipe(first())
      .subscribe(config => this.refreshCustomConfiguration());
  }

  ngOnDestroy(): void {
    this.tenantSubscription?.unsubscribe();
    this.modalClosedSubscription?.unsubscribe();
    this.createUpdateConfigurationSubscription?.unsubscribe();
    this.fetchCustomRanConfigurationSubscription?.unsubscribe();
  }

}
